import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
    Col,
    Form,
    Modal,
    Row,
    InputGroup
} from 'react-bootstrap';
import Tooltip from '@sumologic/yama-ds-components/components/Tooltip';
import { Checkbox, FormControlLabel } from '@sumologic/yama-ds-components';

import Select from '~/components/Select/Select';
import Translate from '~/components/Translate/Translate';
import Icon from '~/components/Icon/Icon';
import Loader from '~/components/Loader/Loader';

import RunbookCanvasContext from '~/containers/RunbookCanvas/RunbookCanvasContext';

import { RE } from '~/modules/helpers/regexp';
import { checkFeatureFlag } from '~/modules/helpers';
import { insertContentAtPosition } from '~/modules/helpers/ckeditor';
import { CHECKBOX, TEXTAREA } from '~/modules/customFieldTypes';

import { getPlaceholderString } from '../RunbookCanvas/Graph/getPlaceholderString';

import ActionFields from './ActionFields';

/**
 * Previous action value button (gear and trash icon beside fields)
 * @param {Array} field - related field
 * @param {Boolean} disabledChange - can change value
 * @param {Array} formProps - form props
 * @param {Function} btnFunction - button functions
 * @returns {React.JSX.Element|null}
 * @author sst
 */
export const previousActionValueButtons = (
    field,
    disabledChange,
    formProps,
    btnFunction
) => {
    const fieldValue = field.value?.value ?? field.value;
    const fieldType = field.type;

    const fieldHasPlaceholder = value => {
        return RE.PLAYBOOK.placeholder.flowOrPlaybookOrPlaybookPayload.test(
            value
        );
    };

    const typesNotSupported = [CHECKBOX];
    const textAreaLoopingEnabled = checkFeatureFlag('textarea_dynamic_array_handling');
    if(!textAreaLoopingEnabled) {
        typesNotSupported.push(TEXTAREA);
    }

    let btnClass = "choose-value-from-action"
    let chooseBtnTooptip = "Choose value from existing action";
    let chooseBtnIcon = "cog"
    if(TEXTAREA === fieldType){
        if (!field.isPartialTextarea) {
            btnClass += ' value-sel-textarea';
        }
        chooseBtnTooptip = "Insert placeholder";
        chooseBtnIcon = "curly-brackets";
    }

    if (typesNotSupported.indexOf(fieldType) === -1) {
        return (
            <>
                {'' !== fieldValue && fieldHasPlaceholder(fieldValue) && (
                    <InputGroup.Text className={btnClass}>
                        <Tooltip
                            title={<Translate message="Remove" />}
                            placement="left"
                            arrow
                        >
                            <span
                                onClick={() => {
                                    btnFunction(field, formProps, 'remove');
                                }}
                            >
                                <Icon type="trash2" />
                            </span>
                        </Tooltip>
                    </InputGroup.Text>
                )}
                {!disabledChange && (
                    <InputGroup.Text className={btnClass}>
                        <Tooltip
                            title={<Translate message={chooseBtnTooptip} />}
                            placement="left"
                            arrow
                        >
                            <span
                                onClick={() => {
                                    btnFunction(field, formProps, 'choose');
                                }}
                            >
                                <Icon type={chooseBtnIcon} />
                            </span>
                        </Tooltip>
                    </InputGroup.Text>
                )}
            </>
        );
    }
};

const ModalBody = ({
    integrationList,
    actionsByType,
    actionFieldsByResource,
    actionList,
    formProps,
    resources,
    setIntegration,
    setAction,
    setActionType,
    setResource,
    setValues,
    values,
    section,
    extraFieldOptions,
    disableChangeAction,
    previousActionButtonFunction,
    isInternalIntegration = false,
    textAreaValSelection,
    fetchingActionFieldsByResource,
    fetchingActionList
}) => {
    const ref = useRef({
        prevResources: resources,
        shouldHandleResourceChange: true
    }).current;

    const [actionsData, setActionsData] = useState(null);

    const runbookContext = useContext(RunbookCanvasContext);

    const disableChange = disableChangeAction; // this info came from the same source

    const handleIntegrationChange = (option = {}) => {
        values.integrationTag = null;
        values.action = null;
        values.actionType = null;
        values.titleBeforeIntegrationChange = values.actionTitle;
        values.actionTitle = null;
        values.actionNameDisplay = null;
        values.resource = null;
        const newActionsData = {
            actionTypeOptions: null,
            actionTypeValue: null
        };
        const newIntegration = option.value;
        setActionsData(newActionsData);
        return setIntegration?.(newIntegration);
    };

    const handleActionTypeChange = (option = {}) => {
        const type = option.value;
        return setActionType?.(type);
    };

    const handleActionChange = (option = {}) => {
        return setAction?.(option.value, option.resource);
    };

    const handleResourceChange = (option = {}) => {
        setResource?.(option.value, null, option.actionInternalName);
    };

    const setFieldValue = (id, value) => {
        const newValues = {
            [id]: value
        };
        setValues(newValues);
        setTimeout(() => {
            if (ref.isMounted) {
                formProps?.validateField(id);
            }
        });
    };

    const resourceHasPlaceholder = value => {
        return RE.PLAYBOOK.placeholder.flowOrPlaybookOrPlaybookPayload.test(
            value
        );
    };

    let actionValue = null;
    const actionOptions = [];
    if (actionsByType?.length) {
        const title = 'actionTitle';
        const nameDisplay = 'actionNameDisplay';
        const key = values.hasOwnProperty(nameDisplay) ? nameDisplay : title;
        const str = values[key]?.trim().toLowerCase();
        for (const item of actionsByType) {
            const label = item.name;
            const option = {
                value: item.internalName,
                label: label
            };
            if (str === label || str === label.toLowerCase()) {
                actionValue = option;
            }
            actionOptions.push(option);
        }
    }

    let resourceValue = null;
    let resourceOptions = [];

    if (resources?.length) {
        const selectedValue = values.resource;

        resourceOptions = [
            {
                id: 'resources',
                label: 'Resources',
                options: resources?.map(resource => {
                    return {
                        value: resource.id,
                        label: resource.name,
                        actionInternalName: resource.actionInternalName || null
                    };
                })
            }
        ];

        if (checkFeatureFlag('dynamic_resource') && 'runbook' === section) {
            resourceOptions = resourceOptions.concat(extraFieldOptions);

            if (!resourceHasPlaceholder(selectedValue)) {
                for (const optGroup of resourceOptions) {
                    for (const option of optGroup.options) {
                        if (
                            option.value.toString() ===
                            selectedValue?.toString()
                        ) {
                            resourceValue = option;
                        }
                    }
                }
            } else {
                resourceValue = selectedValue;
            }
        } else {
            //sst: outside playbook section or feature flag disabled show only resource options
            resourceOptions = resourceOptions[0].options;

            if (resourceHasPlaceholder(selectedValue)) {
                //sst: feature flag disabled && has placeholder -> remove placeholder and select first resource available
                resourceValue = null;
                handleResourceChange(resourceOptions[0]);
            } else {
                for (const option of resourceOptions) {
                    if (option.value.toString() === selectedValue?.toString()) {
                        resourceValue = option;
                    }
                }
            }
        }
    }

    const selectedIntegration = values.integrationTag;
    let integrationSelectValue = null;
    if (integrationList?.length) {
        for (const item of integrationList) {
            if (selectedIntegration === item.value) {
                integrationSelectValue = item;
                break;
            }
        }
    }

    useEffect(() => {
        ref.isMounted = true;
        if (values && Object.keys(values).length) {
            if (integrationList?.length) {
                if (values.integrationTag) {
                    // values.resource is outdated
                    setIntegration(values.integrationTag);
                } else {
                    const resourceId = values.resource;
                    const storedIntegrationTag = values.storedIntegrationTag;
                    if (storedIntegrationTag) {
                        setIntegration(storedIntegrationTag, false);
                    }
                    for (const integration of integrationList) {
                        for (const resource of integration.resources) {
                            if (
                                resourceId?.toString() === resource.toString()
                            ) {
                                setIntegration(integration.value);
                                break;
                            }
                        }
                    }
                }
            } else if (actionList) {
                handleActionTypeChange({
                    value: values.actionType
                }).then(() => {
                    handleActionChange({
                        value: values.action,
                        resource: values.resource
                    });
                });
            }
        }
        return () => {
            ref.isMounted = false;
        };
    }, []);

    useEffect(() => {
        const serialize = JSON.stringify;
        values.actionTitle =
            values.actionTitle || values.titleBeforeIntegrationChange || '';
        if (serialize(ref.prevResources) !== serialize(resources)) {
            ref.prevResources = resources;
            if (ref.shouldHandleResourceChange) {
                ref.shouldHandleResourceChange = false;
                const resourceId = values.resource;
                const selectedResource = resources?.find(
                    e => resourceId === e.id
                );
                const option = { value: resourceId };
                const internalName = selectedResource?.actionInternalName;
                if (internalName) {
                    option.actionInternalName = internalName;
                }
                handleResourceChange(option);
            }
        }
    }, [resources]);

    useEffect(() => {
        if (actionList) {
            const actionListEntries = Object.entries(actionList);

            if (actionListEntries.length) {
                let selectedOption;
                const valuesActionType = values.actionType; // 𝘵𝘩𝘦 𝘴𝘦𝘭𝘦𝘤𝘵𝘦𝘥 𝘰𝘯𝘦
                const options = [];
                for (const [key, value] of actionListEntries) {
                    if (value?.length) {
                        const option = {
                            value: key,
                            label: key
                        };
                        options.push(option);
                        if (valuesActionType && key === valuesActionType) {
                            selectedOption = option;
                        }
                    }
                }

                if (!selectedOption) {
                    const firstOptionAvailable = options[0];
                    selectedOption = firstOptionAvailable || { value: {} };
                }
                const newActionsData = {
                    actionTypeOptions: options,
                    actionTypeValue: selectedOption
                };
                setActionsData(newActionsData);
                handleActionTypeChange({
                    value: selectedOption.value
                }).then(() => {
                    const { action, resource, actionTitle } = values;
                    if (action && resource && actionTitle) {
                        handleActionChange({
                            value: action,
                            resource: resource
                        });
                    }
                });
            }
        }
    }, [actionList]);

    useEffect(() => {
        if (values.actionType && actionsData) {
            const newActionsData = {
                actionTypeOptions: actionsData.actionTypeOptions,
                actionTypeValue: {
                    label: values.actionType,
                    value: values.actionType
                }
            };
            setActionsData(newActionsData);
        }
    }, [values.actionType]);

    const onTextAreaReady = (cke, fieldId) => {
        if (
            textAreaValSelection?.value &&
            textAreaValSelection.id === fieldId &&
            cke
        ) {
            insertContentAtPosition(cke, textAreaValSelection.value, textAreaValSelection.path);
        }
    };

    return (
        <Modal.Body
            className={`${
                section === 'runbook' ? 'p-5 ' : ''
            }flex-column d-flex`}
        >
            {integrationList?.length > 0 && (
                <Row className="mb-4">
                    <Col>
                        <Form.Group
                            controlId="integration"
                            className="form-group"
                        >
                            <Form.Label required>
                                <Translate message="Integration" />
                            </Form.Label>
                            <Select
                                isDisabled={disableChangeAction}
                                options={integrationList}
                                value={integrationSelectValue}
                                onChange={handleIntegrationChange}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            )}
            {fetchingActionList && !Object.keys(actionList).length && (
                <Loader />
            )}
            {actionsData && (
                <Row className="mb-4">
                    <Col>
                        <Form.Group
                            controlId="actionType"
                            className="form-group"
                        >
                            <Form.Label required>
                                <Translate message="Type" />
                            </Form.Label>
                            <Select
                                isDisabled={disableChangeAction}
                                options={actionsData.actionTypeOptions}
                                value={actionsData.actionTypeValue}
                                onChange={handleActionTypeChange}
                            />
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group
                            controlId="actionName"
                            className="form-group"
                        >
                            <Form.Label required>
                                <Translate message="Action" />
                            </Form.Label>
                            <Select
                                isDisabled={disableChangeAction}
                                options={actionOptions}
                                value={actionValue}
                                onChange={handleActionChange}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            )}
            {!isInternalIntegration && actionValue && (
                <Row className="mb-4">
                    <Col>
                        <div className="resource-field">
                            <Form.Group
                                controlId="resource"
                                className="form-group mb-3"
                            >
                                <Form.Label required>
                                    <Translate message="Resource" />
                                </Form.Label>
                                <InputGroup>
                                    {checkFeatureFlag('dynamic_resource') &&
                                    resourceHasPlaceholder(resourceValue) ? (
                                        getPlaceholderString(
                                            resourceValue,
                                            runbookContext
                                        )
                                    ) : (
                                        <Select
                                            isDisabled={disableChangeAction}
                                            options={resourceOptions}
                                            value={resourceValue}
                                            onChange={handleResourceChange}
                                        />
                                    )}
                                    {checkFeatureFlag('dynamic_resource') &&
                                        'runbook' === section &&
                                        previousActionValueButtons(
                                            {
                                                id: 'resource',
                                                value: resourceValue
                                            },
                                            disableChange,
                                            formProps,
                                            previousActionButtonFunction
                                        )}
                                </InputGroup>
                            </Form.Group>
                            {checkFeatureFlag('dynamic_resource') &&
                                'runbook' === section && (
                                    <Form.Group>
                                        <FormControlLabel
                                            className="d-inline"
                                            control={
                                                <Checkbox
                                                    disabled={disableChangeAction}
                                                    checked={
                                                        formProps.values
                                                            .getDefaultResource
                                                    }
                                                    onChange={event => {
                                                        formProps.setValues({
                                                            ...formProps.values,
                                                            getDefaultResource:
                                                                event.target.checked
                                                        });
                                                    }}
                                                    inputProps={{ id: 'resource-default-checkbox' }}
                                                />
                                            }
                                            label="Use default resource"
                                        />
                                        <Tooltip
                                            title={<Translate message="In case of failed resource matching the default resource will be used" />}
                                            placement="bottom"
                                            arrow
                                        >
                                            <Icon
                                                type="information"
                                                className="mx-2"
                                            />
                                        </Tooltip>
                                    </Form.Group>
                                )}
                        </div>
                    </Col>
                </Row>
            )}
            {actionFieldsByResource?.length > 0 && (
                <Row className="mt-4">
                    <Col>
                        <ActionFields
                            section={section}
                            validateOnFieldsChange={false}
                            fields={actionFieldsByResource}
                            formProps={formProps}
                            setFieldValue={setFieldValue}
                            disableChange={disableChange}
                            onTextAreaReady={onTextAreaReady}
                            previousActionButtonFunction={
                                previousActionButtonFunction
                            }
                        />
                    </Col>
                </Row>
            )}
            {fetchingActionFieldsByResource &&
                !actionFieldsByResource?.length && <Loader />}
        </Modal.Body>
    );
};

ModalBody.propTypes = {
    integrationList: PropTypes.array,
    actionsByType: PropTypes.array.isRequired,
    actionFieldsByResource: PropTypes.array.isRequired,
    actionList: PropTypes.object,
    formProps: PropTypes.object.isRequired,
    resources: PropTypes.array.isRequired,
    setIntegration: PropTypes.func,
    setAction: PropTypes.func.isRequired,
    setActionType: PropTypes.func.isRequired,
    setResource: PropTypes.func.isRequired,
    setValues: PropTypes.func.isRequired,
    values: PropTypes.object.isRequired,
    section: PropTypes.string,
    disableChangeAction: PropTypes.bool,
    previousActionButtonFunction: PropTypes.func,
    fetchingActionFieldsByResource: PropTypes.bool,
    fetchingActionList: PropTypes.bool
};

export default ModalBody;
